// src/js/modules/hotspotTooltips.js

/**
 * One label at a time, on the hotspot you are looking at.
 *
 * As the panorama rotates, the named place nearest the centre of the view labels itself; hovering
 * a different hotspot hands the label to that one instead. Never more than one is on screen.
 *
 * Why this is ours rather than the skin's: the skin's own tooltips are wired per hotspot inside a
 * closure (`svg.onmouseenter` -> `elementMouseOver[key]` -> `tt_x.logicBlock_visible()`), with no
 * handle reachable from here, and skin.js is generated from the .ggsk -- so it is not something to
 * reach into or patch. The skin's hover tooltips are expected to be switched off in Pano2VR; this
 * replaces them.
 *
 * Positions come from the player, not the DOM: getHotspot() gives each hotspot's own pan/tilt, so
 * "nearest the centre" is angular distance on a sphere rather than pixel measurement. The DOM is
 * touched only to place the single visible label and to detect hover.
 */

import { translations, getLangFromURL } from '../map/i18n.js';

/**
 * Hotspot kinds that get a label.
 *
 * Named places only -- departments, library, gym, bus, parking. Navigation arrows (ht3d, ht_node),
 * ramps and entrances are deliberately excluded: they are dense, mostly unnamed, and labelling them
 * would fire constantly while rotating.
 *
 * Note the trailing underscore on the prefix. `ht_infoentance` is an entrance, not an info point,
 * and a bare 'ht_info' prefix would sweep all 28 of them in.
 */
const LABELLED_PREFIXES = ['ht_info_', 'ht_uoc_'];
const LABELLED_EXACT = ['ht_info', 'ht_uoc', 'ht_welcome'];

/** Widest angle from the view centre that still earns a label, as a fraction of the current fov. */
const CENTRE_FRACTION = 0.35;

/** ...but never wider than this, however far out the view is zoomed. */
const MAX_CENTRE_ANGLE = 30;

const DEG = Math.PI / 180;

/**
 * The label for a hotspot that carries no title of its own.
 *
 * 41 hotspots in the labelled set have an empty title in pano.xml -- 23 parking, 9 library,
 * 7 bus, a restaurant and one campus marker. They are interchangeable instances of a kind rather
 * than named places, so naming each one individually in Pano2VR would be data entry with no
 * payoff. Their kind is label enough, and it comes from the Skin-ID.
 *
 * This is what lets the skin's own tooltips be removed outright: without it, deleting them would
 * leave those 41 silent.
 *
 * @param {string} skinid Hotspot skin id.
 * @param {Object} strings Translation table for the active language.
 * @return {string} Label, or '' when the kind has no generic name.
 */
function genericLabel(skinid, strings) {
    const kinds = (strings && strings.hotspotKinds) || {};

    return kinds[skinid] || '';
}

/**
 * Whether a hotspot kind is one we label.
 *
 * @param {string} skinid Hotspot skin id.
 * @return {boolean} True when it should get a label.
 */
function isLabelled(skinid) {
    if (!skinid) return false;
    if (LABELLED_EXACT.indexOf(skinid) !== -1) return true;

    return LABELLED_PREFIXES.some((prefix) => skinid.indexOf(prefix) === 0);
}

/**
 * Angular distance between two directions on the sphere, in degrees.
 *
 * Plain pan subtraction is wrong near the poles and wraps badly -- hotspot pans in this project run
 * to -314 -- so this is the real great-circle angle.
 *
 * @param {number} pan1  First pan, degrees.
 * @param {number} tilt1 First tilt, degrees.
 * @param {number} pan2  Second pan, degrees.
 * @param {number} tilt2 Second tilt, degrees.
 * @return {number} Angle between them, degrees.
 */
function angleBetween(pan1, tilt1, pan2, tilt2) {
    const t1 = tilt1 * DEG;
    const t2 = tilt2 * DEG;
    const dp = (pan1 - pan2) * DEG;

    const cos = Math.sin(t1) * Math.sin(t2) + Math.cos(t1) * Math.cos(t2) * Math.cos(dp);

    // Guard the domain: accumulated float error can push this a hair past 1 and yield NaN.
    return Math.acos(Math.min(1, Math.max(-1, cos))) / DEG;
}

export class HotspotTooltips {
    constructor(pano) {
        this.pano = pano;
        this.lang = getLangFromURL('el');
        this.t = translations[this.lang];
        this.hotspots = [];
        this.hovered = null;
        this.shown = null;
        this.element = null;
        this.last = { pan: null, tilt: null, fov: null };
        this.frame = null;
        this.inner = null;
        this.dragFrom = null;
        this.pointer = null;
    }

    init() {
        if (!this.pano) return;

        this.injectHTML();
        this.guardDrags();
        this.collect();

        this.pano.on('changenode', () => {
            // The old node's hotspot elements are gone; anything pointing at them must go too.
            this.hovered = null;
            this.hide();
            // Hotspot elements are rebuilt by the skin slightly after the event.
            setTimeout(() => this.collect(), 300);
        });

        this.loop();
    }

    /**
     * Two nested elements on purpose.
     *
     * The outer one is positioned at the hotspot and carries a translate that centres it there,
     * so it cannot also be used for the entry animation without the two transforms fighting. The
     * inner one holds the visible box and does all the moving.
     */
    injectHTML() {
        let el = document.getElementById('uoc-hotspot-tooltip');

        if (!el) {
            el = document.createElement('div');
            el.className = 'uoc-hotspot-tooltip';
            el.id = 'uoc-hotspot-tooltip';
            el.setAttribute('aria-hidden', 'true');
            el.innerHTML = '<div class="uoc-hotspot-tooltip__inner"></div>';
            document.body.appendChild(el);
        }

        this.element = el;
        this.inner = el.querySelector('.uoc-hotspot-tooltip__inner');
    }

    /**
     * Make the link clickable without ever letting it touch the mouse.
     *
     * The obvious implementation -- pointer-events:auto on the anchor -- is wrong here, and
     * measurably so: the tooltip can be on screen merely because you are facing a hotspot, so its
     * link sits under the cursor in the middle of the view. Give it pointer-events and it
     * swallows the mousedown, the player never sees the press, and the panorama stops rotating
     * wherever the label happens to be. Tested: pan did not move at all.
     *
     * So the whole tooltip stays inert and the click is reconstructed here instead. Every press
     * reaches the player as usual; afterwards, if the pointer never travelled far enough to be a
     * drag and it came up inside the link's box, that counts as a click on the link.
     */
    guardDrags() {
        const linkRect = () => {
            if (!this.element || !this.element.classList.contains('is-visible')) return null;

            const link = this.element.querySelector('.uoc-hotspot-tooltip__link');

            return link ? link.getBoundingClientRect() : null;
        };

        const inside = (rect, x, y) =>
            !!rect && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

        document.addEventListener('mousedown', (event) => {
            this.dragFrom = { x: event.clientX, y: event.clientY };
        }, true);

        document.addEventListener('mouseup', (event) => {
            const from = this.dragFrom;
            this.dragFrom = null;

            if (!from) return;

            const dx = event.clientX - from.x;
            const dy = event.clientY - from.y;

            // A press that travelled is a rotation, not a click, however it ended.
            if (Math.sqrt(dx * dx + dy * dy) > 5) return;
            if (!inside(linkRect(), event.clientX, event.clientY)) return;

            const link = this.element.querySelector('.uoc-hotspot-tooltip__link');
            const href = link && link.getAttribute('href');

            if (href) window.open(href, '_blank', 'noopener');
        }, true);

        // The pointer never enters the tooltip, so nothing inside it can ever be :hover, receive
        // mouseenter, or set a cursor. Every one of those is driven from the pointer position
        // tracked here instead.
        document.addEventListener('mousemove', (event) => {
            this.pointer = { x: event.clientX, y: event.clientY };

            const over = inside(linkRect(), event.clientX, event.clientY);

            document.body.classList.toggle('uoc-hotspot-link-hover', over);

            const link = this.element && this.element.querySelector('.uoc-hotspot-tooltip__link');

            if (link) link.classList.toggle('is-hot', over);
        }, true);
    }

    /**
     * Build the labelled-hotspot list for the current node.
     *
     * getPointHotspotIds() gives the ids from pano.xml and getCurrentPointHotspots() gives the
     * skin's DOM elements for the same node. They are index-aligned -- verified against the running
     * tour by matching each element's ggId to the corresponding hotspot's skinid -- which is the
     * only way to get from a hotspot's data to the element that represents it. The length check
     * below is there because that alignment is an observation, not a documented guarantee: if it
     * ever stops holding, labels simply stop following hover rather than attaching to the wrong
     * hotspot.
     */
    collect() {
        this.hotspots = [];

        if (!this.pano.getPointHotspotIds) return;

        const ids = this.pano.getPointHotspotIds() || [];
        const elements = (this.pano.getCurrentPointHotspots && this.pano.getCurrentPointHotspots()) || [];
        const aligned = elements.length === ids.length;

        ids.forEach((id, index) => {
            const data = this.pano.getHotspot ? this.pano.getHotspot(id) : null;

            if (!data || !isLabelled(data.skinid)) return;

            // Its own title when it has one, otherwise the name of its kind.
            const title = String(data.title || '').trim() || genericLabel(data.skinid, this.t);

            if (!title) return;

            const element = aligned ? elements[index] : null;

            const url = String(data.url || '').trim();

            const record = {
                id,
                title,
                url: /^https?:\/\//i.test(url) ? url : '',
                pan: Number(data.pan) || 0,
                tilt: Number(data.tilt) || 0,
                element,
            };

            if (element) {
                element.addEventListener('mouseenter', () => {
                    this.hovered = record;
                });
                element.addEventListener('mouseleave', () => {
                    if (this.hovered === record) this.hovered = null;
                });
            }

            this.hotspots.push(record);
        });
    }

    /**
     * Whether the pointer is resting on the label itself.
     *
     * Moving from a hotspot onto its own label used to make the label vanish: leaving the hotspot
     * cleared the hover, the centre pick took over, and a link you were reaching for disappeared
     * from under the cursor. The label has to count as part of what you are hovering.
     *
     * The box is inflated because the label sits 14px clear of its hotspot, and without slack the
     * pointer crosses a gap where it is on neither and the label drops in between.
     *
     * @return {boolean} True when the pointer is on or near the visible label.
     */
    pointerHoldsLabel() {
        if (!this.shown || !this.pointer || !this.element) return false;
        if (!this.element.classList.contains('is-visible')) return false;

        const rect = this.inner.getBoundingClientRect();
        const slack = 22;

        return (
            this.pointer.x >= rect.left - slack &&
            this.pointer.x <= rect.right + slack &&
            this.pointer.y >= rect.top - slack &&
            this.pointer.y <= rect.bottom + slack
        );
    }

    /**
     * The hotspot nearest the centre of the view, if any is close enough.
     *
     * @return {Object|null} Hotspot record.
     */
    nearestToCentre() {
        if (!this.hotspots.length) return null;

        const pan = this.pano.getPan();
        const tilt = this.pano.getTilt();
        const fov = this.pano.getFov() || 70;

        // Tie the catchment to the zoom: at a narrow fov a hotspot 30 degrees away is off screen,
        // and labelling it would name something the visitor cannot see.
        const limit = Math.min(fov * CENTRE_FRACTION, MAX_CENTRE_ANGLE);

        let best = null;
        let bestAngle = Infinity;

        this.hotspots.forEach((hotspot) => {
            const angle = angleBetween(hotspot.pan, hotspot.tilt, pan, tilt);

            if (angle < bestAngle) {
                bestAngle = angle;
                best = hotspot;
            }
        });

        return bestAngle <= limit ? best : null;
    }

    /**
     * Fill the box for a hotspot.
     *
     * 26 of the labelled hotspots carry a url -- the gym, the library and the welcome office.
     * pano.xml gives them target="_parent", which in a tour that is its own page means replacing
     * the tour. Opened in a new tab instead, so nobody loses their place mid-visit, and with
     * noopener because these are external sites.
     *
     * @param {Object} hotspot Hotspot record.
     */
    render(hotspot) {
        const label = '<span class="uoc-hotspot-tooltip__label"></span>';

        if (!hotspot.url) {
            this.inner.innerHTML = label;
            this.inner.querySelector('.uoc-hotspot-tooltip__label').textContent = hotspot.title;
            this.element.classList.remove('has-link');
            return;
        }

        this.inner.innerHTML =
            '<a class="uoc-hotspot-tooltip__link" target="_blank" rel="noopener noreferrer">' +
            label +
            '<svg class="uoc-hotspot-tooltip__icon" viewBox="0 0 24 24" aria-hidden="true">' +
            '<path d="M14 4h6v6M20 4l-8.5 8.5M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/>' +
            '</svg></a>';

        const link = this.inner.querySelector('.uoc-hotspot-tooltip__link');
        link.href = hotspot.url;
        link.title = hotspot.title;
        this.inner.querySelector('.uoc-hotspot-tooltip__label').textContent = hotspot.title;
        this.element.classList.add('has-link');
    }

    /**
     * Place the label above its hotspot and show it.
     *
     * @param {Object} hotspot Hotspot record.
     */
    show(hotspot) {
        const el = this.element;
        if (!el) return;

        if (this.shown !== hotspot) {
            this.render(hotspot);

            // Replay the entry animation when the label hands over to a different hotspot, so a
            // swap reads as a new thing arriving rather than text silently changing.
            if (el.classList.contains('is-visible')) {
                el.classList.remove('is-visible');
                // Forcing layout between the two is what makes the browser start the transition
                // again rather than coalescing the removal and the re-add into nothing.
                void el.offsetWidth;
                el.classList.add('is-visible');
            }

            this.shown = hotspot;
        }

        // The one DOM read in the whole loop, and only for the hotspot actually being labelled.
        // The player rewrites these positions every frame, so it has to be read fresh each time.
        let x = window.innerWidth / 2;
        let y = window.innerHeight / 2;

        if (hotspot.element) {
            const rect = hotspot.element.getBoundingClientRect();

            if (rect.width || rect.height) {
                x = rect.left + rect.width / 2;
                y = rect.top;
            } else {
                // Hotspot containers are often zero-sized with the artwork in a child.
                const art = hotspot.element.querySelector('img, .ggskin_svg');

                if (art) {
                    const artRect = art.getBoundingClientRect();
                    x = artRect.left + artRect.width / 2;
                    y = artRect.top;
                }
            }
        }

        el.style.left = Math.round(x) + 'px';
        el.style.top = Math.round(y) + 'px';
        el.classList.add('is-visible');
        el.setAttribute('aria-hidden', 'false');
    }

    hide() {
        if (!this.element) return;

        this.element.classList.remove('is-visible');
        this.element.setAttribute('aria-hidden', 'true');
        this.shown = null;
    }

    /**
     * Decide what to label, every frame.
     *
     * A frame loop rather than a player event: the player exposes no view-changed event by a name
     * worth depending on, and this early-outs to three getters when nothing has moved.
     */
    loop() {
        const tick = () => {
            this.frame = requestAnimationFrame(tick);

            if (!this.pano || !this.pano.getPan) return;

            const pan = this.pano.getPan();
            const tilt = this.pano.getTilt();
            const fov = this.pano.getFov();
            const moved = pan !== this.last.pan || tilt !== this.last.tilt || fov !== this.last.fov;

            this.last = { pan, tilt, fov };

            // Nothing to recompute when nothing moved, hover has not changed, and the pointer is
            // not resting on the label -- that last one can change with no view movement at all.
            if (!moved && this.hovered === this.shown && !this.pointerHoldsLabel()) return;

            /*
             * Hover beats the centre pick -- pointing at something is a stronger signal than
             * happening to face it -- and resting on the label counts as still pointing at it,
             * so it survives the trip from hotspot to link.
             */
            const target =
                this.hovered ||
                (this.pointerHoldsLabel() ? this.shown : null) ||
                this.nearestToCentre();

            if (target) {
                this.show(target);
            } else {
                this.hide();
            }
        };

        this.frame = requestAnimationFrame(tick);
    }

    /**
     * Switch language, which changes the generic labels.
     *
     * @param {string} lang Language code.
     */
    setLanguage(lang) {
        if (!translations[lang]) lang = 'el';

        this.lang = lang;
        this.t = translations[lang];

        // Titled hotspots are unaffected, but the generic ones have to be relabelled.
        this.hide();
        this.collect();
    }

    destroy() {
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.element && this.element.parentNode) this.element.parentNode.removeChild(this.element);
    }
}
